import { TimelineChangeValidationError } from './errors.js';
export const normalizeInsertIndex = (length, index) => {
    if (index === undefined) {
        return length;
    }
    if (index < 0) {
        return 0;
    }
    if (index > length) {
        return length;
    }
    return index;
};
export const normalizeItemEndDate = (item) => {
    const nextItem = Object.assign({}, item);
    if (!nextItem.endDate) {
        delete nextItem.endDate;
    }
    return nextItem;
};
export const cloneSection = (section) => {
    return Object.assign(Object.assign({}, section), { items: section.items.map((item) => (Object.assign({}, item))) });
};
export const getActiveSections = (data) => data.pages[0].sections;
export const withUpdatedSections = (data, sections) => (Object.assign(Object.assign({}, data), { pages: [Object.assign(Object.assign({}, data.pages[0]), { sections })] }));
export const findSectionIndex = (current, sectionId) => {
    return getActiveSections(current).findIndex((section) => section.id === sectionId);
};
export const findItemLocation = (current, itemId, expectedSectionId) => {
    const sections = getActiveSections(current);
    const sectionCandidates = expectedSectionId
        ? sections
            .map((section, sectionIndex) => ({ section, sectionIndex }))
            .filter((entry) => entry.section.id === expectedSectionId)
        : sections.map((section, sectionIndex) => ({ section, sectionIndex }));
    for (const { section, sectionIndex } of sectionCandidates) {
        const itemIndex = section.items.findIndex((item) => item.id === itemId);
        if (itemIndex === -1) {
            continue;
        }
        return {
            sectionIndex,
            section,
            itemIndex,
            item: section.items[itemIndex],
        };
    }
    return null;
};
const applyAddSection = (current, change) => {
    var _a;
    const sections = getActiveSections(current);
    if (findSectionIndex(current, change.sectionId) !== -1) {
        throw new TimelineChangeValidationError(`Section already exists: ${change.sectionId}`);
    }
    const targetIndex = normalizeInsertIndex(sections.length, change.index);
    const nextSections = [...sections];
    nextSections.splice(targetIndex, 0, Object.assign({ id: change.sectionId, label: change.label, items: (_a = change.items) !== null && _a !== void 0 ? _a : [] }, (change.hidden ? { hidden: change.hidden } : {})));
    return withUpdatedSections(current, nextSections);
};
const applyRemoveSection = (current, change) => {
    const sectionIndex = findSectionIndex(current, change.sectionId);
    if (sectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    const nextSections = getActiveSections(current).filter((section) => section.id !== change.sectionId);
    if (nextSections.length === 0) {
        throw new TimelineChangeValidationError('Cannot remove the last section');
    }
    return withUpdatedSections(current, nextSections);
};
const applyUpdateSectionLabel = (current, change) => {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, change.sectionId);
    if (sectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    const section = sections[sectionIndex];
    if (section.label === change.nextLabel) {
        throw new TimelineChangeValidationError('Section label change is a no-op');
    }
    return withUpdatedSections(current, sections.map((entry, index) => (index === sectionIndex ? Object.assign(Object.assign({}, entry), { label: change.nextLabel }) : entry)));
};
const applyMoveSection = (current, change) => {
    const sections = getActiveSections(current);
    const fromIndex = findSectionIndex(current, change.sectionId);
    if (fromIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    const targetIndex = normalizeInsertIndex(sections.length - 1, change.toIndex);
    if (fromIndex === targetIndex) {
        throw new TimelineChangeValidationError('Move section is a no-op');
    }
    const nextSections = [...sections];
    const [moved] = nextSections.splice(fromIndex, 1);
    nextSections.splice(targetIndex, 0, moved);
    return withUpdatedSections(current, nextSections);
};
const applyUpdateItemTitle = (current, change) => {
    var _a;
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, change.sectionId);
    if (sectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    const section = sections[sectionIndex];
    const itemIndex = section.items.findIndex((item) => item.id === change.itemId);
    if (itemIndex === -1) {
        throw new TimelineChangeValidationError(`Item not found: ${change.itemId}`);
    }
    if (((_a = section.items[itemIndex]) === null || _a === void 0 ? void 0 : _a.title) === change.nextTitle) {
        throw new TimelineChangeValidationError('Item title change is a no-op');
    }
    return withUpdatedSections(current, sections.map((entry, index) => {
        if (index !== sectionIndex) {
            return entry;
        }
        return Object.assign(Object.assign({}, entry), { items: entry.items.map((item) => (item.id === change.itemId ? Object.assign(Object.assign({}, item), { title: change.nextTitle }) : item)) });
    }));
};
const applyCreateItem = (current, change) => {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, change.sectionId);
    if (sectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    const section = sections[sectionIndex];
    if (section.items.some((item) => item.id === change.item.id)) {
        throw new TimelineChangeValidationError(`Item already exists: ${change.item.id}`);
    }
    const targetIndex = normalizeInsertIndex(section.items.length, change.index);
    const nextSections = sections.map((entry, index) => {
        if (index !== sectionIndex) {
            return entry;
        }
        const nextItems = [...entry.items];
        nextItems.splice(targetIndex, 0, normalizeItemEndDate(change.item));
        return Object.assign(Object.assign({}, entry), { items: nextItems });
    });
    return withUpdatedSections(current, nextSections);
};
const applyChangeItemDateRange = (current, change) => {
    var _a, _b, _c, _d, _e;
    const source = findItemLocation(current, change.itemId);
    if (!source) {
        throw new TimelineChangeValidationError(`Item not found: ${change.itemId}`);
    }
    const targetSectionIndex = findSectionIndex(current, change.nextRange.sectionId);
    if (targetSectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.nextRange.sectionId}`);
    }
    const hasSameRange = (source.section.id === change.nextRange.sectionId
        && source.item.startDate === change.nextRange.startDate
        && source.item.endDate === change.nextRange.endDate);
    if (hasSameRange) {
        throw new TimelineChangeValidationError('Item date range change is a no-op');
    }
    const nextSections = getActiveSections(current).map(cloneSection);
    const [removedItem] = (_b = (_a = nextSections[source.sectionIndex]) === null || _a === void 0 ? void 0 : _a.items.splice(source.itemIndex, 1)) !== null && _b !== void 0 ? _b : [];
    if (!removedItem) {
        throw new TimelineChangeValidationError(`Item not found: ${change.itemId}`);
    }
    const movedItem = normalizeItemEndDate(Object.assign(Object.assign({}, removedItem), { startDate: change.nextRange.startDate, endDate: change.nextRange.endDate }));
    let insertionBaseLength = (_d = (_c = nextSections[targetSectionIndex]) === null || _c === void 0 ? void 0 : _c.items.length) !== null && _d !== void 0 ? _d : 0;
    if (source.sectionIndex === targetSectionIndex && source.itemIndex < insertionBaseLength) {
        insertionBaseLength -= 1;
    }
    const insertionIndex = normalizeInsertIndex(insertionBaseLength, change.targetIndex);
    (_e = nextSections[targetSectionIndex]) === null || _e === void 0 ? void 0 : _e.items.splice(insertionIndex, 0, movedItem);
    return withUpdatedSections(current, nextSections);
};
const applyDeleteItem = (current, change) => {
    const location = findItemLocation(current, change.itemId, change.sectionId);
    if (!location) {
        throw new TimelineChangeValidationError(`Item not found: ${change.itemId}`);
    }
    return withUpdatedSections(current, getActiveSections(current).map((section, sectionIndex) => {
        if (sectionIndex !== location.sectionIndex) {
            return section;
        }
        return Object.assign(Object.assign({}, section), { items: section.items.filter((item) => item.id !== change.itemId) });
    }));
};
const applyHideSection = (current, change) => {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, change.sectionId);
    if (sectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    if (sections[sectionIndex].hidden) {
        throw new TimelineChangeValidationError('Section is already hidden');
    }
    return withUpdatedSections(current, sections.map((entry, index) => (index === sectionIndex ? Object.assign(Object.assign({}, entry), { hidden: true }) : entry)));
};
const applyShowSection = (current, change) => {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, change.sectionId);
    if (sectionIndex === -1) {
        throw new TimelineChangeValidationError(`Section not found: ${change.sectionId}`);
    }
    if (!sections[sectionIndex].hidden) {
        throw new TimelineChangeValidationError('Section is already visible');
    }
    return withUpdatedSections(current, sections.map((entry, index) => (index === sectionIndex ? Object.assign(Object.assign({}, entry), { hidden: undefined }) : entry)));
};
const applyReplaceSnapshot = (current, change) => {
    const nextSnapshot = change.snapshot;
    if (JSON.stringify(current) === JSON.stringify(nextSnapshot)) {
        throw new TimelineChangeValidationError('Replace snapshot is a no-op');
    }
    return nextSnapshot;
};
const applyChange = (current, change) => {
    switch (change.type) {
        case 'add-section':
            return applyAddSection(current, change);
        case 'remove-section':
            return applyRemoveSection(current, change);
        case 'update-section-label':
            return applyUpdateSectionLabel(current, change);
        case 'move-section':
            return applyMoveSection(current, change);
        case 'update-item-title':
            return applyUpdateItemTitle(current, change);
        case 'create-item':
            return applyCreateItem(current, change);
        case 'change-item-date-range':
            return applyChangeItemDateRange(current, change);
        case 'delete-item':
            return applyDeleteItem(current, change);
        case 'hide-section':
            return applyHideSection(current, change);
        case 'show-section':
            return applyShowSection(current, change);
        case 'replace-snapshot':
            return applyReplaceSnapshot(current, change);
        default: {
            const exhaustiveCheck = change;
            throw new TimelineChangeValidationError(`Unsupported change type: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};
export const applyTimelineChanges = (current, changes) => {
    return changes.reduce((snapshot, change) => {
        return applyChange(snapshot, change);
    }, current);
};
